import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faClipboardList, faCode } from '@fortawesome/free-solid-svg-icons';

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/YOUR_FORM_ID'; // TODO: replace with your real Formspree form ID once you create an account at formspree.io

const jobs = [
  {
    icon: faChartLine,
    title: 'Business Development Manager — Real Estate',
    type: 'Full-time · Pune',
    details: [
      'Drive B2B and B2C sales for our upcoming Real Estate AI platform, building relationships with brokers, developers, and channel partners.',
      'Own the full sales cycle — lead generation, pitching, negotiation, and closing.',
      '3+ years of experience in real estate sales, broking, or business development preferred.',
      'Strong communication skills and a network within the real estate ecosystem is a big plus.',
      'Comfortable working closely with product and tech teams to relay market feedback.',
    ],
  },
  {
    icon: faClipboardList,
    title: 'Office Admin',
    type: 'Full-time · Pune',
    details: [
      'Manage day-to-day office operations, vendor coordination, and administrative support.',
      'Handle scheduling, documentation, and basic HR/finance coordination.',
      'Organized, dependable, and comfortable working independently.',
      'Prior admin/office management experience preferred but not mandatory.',
    ],
  },
  {
    icon: faCode,
    title: 'Software Interns',
    type: 'Internship · 6 months – 1 year · Pune',
    details: [
      'Work directly with our engineering team on live projects, including our Real Estate AI platform.',
      'Open to students and graduates across Computer Science, Engineering, and MBA backgrounds — MBA candidates are also welcome.',
      'Candidates will be tested on analytical thinking and mathematics as part of the selection process.',
      'No formal stipend structure has been finalized — this will be discussed during the interview process.',
      'A genuine interest in AI-assisted software development and a willingness to learn fast matters more than a specific degree.',
    ],
  },
];

function JobCard({ job, i, onApply }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      className="tool-card card overflow-hidden text-center p-8 flex flex-col items-center"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.5, delay: i * 0.1 }}
    >
      <div
        className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5"
        style={{ background: 'linear-gradient(135deg, var(--accent), var(--accent-muted))' }}
      >
        <FontAwesomeIcon icon={job.icon} className="text-2xl" style={{ color: 'var(--bg)' }} />
      </div>
      <h3 className="t-h2 mb-1" style={{ color: 'var(--text-primary)' }}>{job.title}</h3>
      <span className="t-sm mb-4" style={{ color: 'var(--accent)' }}>{job.type}</span>

      {open && (
        <div className="text-left w-full mt-2">
          <ul className="beyond-list mb-6 space-y-2">
            {job.details.map((d, idx) => (
              <li key={idx}>{d}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex items-center gap-3 mt-2">
        <button
          className="t-sm underline-offset-4 hover:underline"
          style={{ color: 'var(--text-muted)' }}
          onClick={() => setOpen((o) => !o)}
        >
          {open ? 'Show less' : 'View details'}
        </button>
        <button className="btn btn-primary" onClick={() => onApply(job.title)}>
          Apply for this role
        </button>
      </div>
    </motion.div>
  );
}

export default function Careers() {
  const [selectedJob, setSelectedJob] = useState('');
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState('idle');

  const handleApply = (title) => {
    setSelectedJob(title);
    const el = document.getElementById('careers-form');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: (() => {
          const fd = new FormData();
          fd.append('role', selectedJob || 'General Enquiry');
          fd.append('name', form.name);
          fd.append('email', form.email);
          fd.append('message', form.message);
          return fd;
        })(),
      });
      if (res.ok) {
        setStatus('sent');
        setForm({ name: '', email: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="careers" className="py-24 px-6 md:px-8" style={{ backgroundColor: 'var(--bg)' }}>
      <div className="container-premium">
        <div className="eyebrow mb-4">Careers</div>
        <h2 className="t-h1 font-display mb-4" style={{ color: 'var(--text-primary)' }}>
          Join Ufeus
        </h2>
        <p className="t-body mb-14 max-w-2xl" style={{ color: 'var(--text-secondary)' }}>
          We're currently hiring for three roles. No degree required — just curiosity, creativity, and a
          willingness to learn.
        </p>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {jobs.map((job, i) => (
            <JobCard key={job.title} job={job} i={i} onApply={handleApply} />
          ))}
        </div>

        <div id="careers-form" className="card p-8 max-w-2xl mx-auto">
          <h3 className="t-h2 mb-6" style={{ color: 'var(--text-primary)' }}>
            {selectedJob ? `Apply — ${selectedJob}` : 'Send Us Your Details'}
          </h3>
          <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
            <label className="flex flex-col t-sm gap-1" style={{ color: 'var(--text-secondary)' }}>
              Name
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                className="mt-1 px-4 h-11 rounded-ctl border t-body"
                style={{ backgroundColor: 'var(--bg-elevated)', borderColor: 'var(--border-medium)', color: 'var(--text-primary)' }}
              />
            </label>
            <label className="flex flex-col t-sm gap-1" style={{ color: 'var(--text-secondary)' }}>
              Email
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                required
                className="mt-1 px-4 h-11 rounded-ctl border t-body"
                style={{ backgroundColor: 'var(--bg-elevated)', borderColor: 'var(--border-medium)', color: 'var(--text-primary)' }}
              />
            </label>
            <label className="flex flex-col t-sm gap-1" style={{ color: 'var(--text-secondary)' }}>
              Message
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                rows={4}
                className="mt-1 px-4 py-2 rounded-ctl border t-body"
                style={{ backgroundColor: 'var(--bg-elevated)', borderColor: 'var(--border-medium)', color: 'var(--text-primary)' }}
              />
            </label>
            <button type="submit" className="btn btn-primary mt-2" disabled={status === 'sending'}>
              {status === 'sending' ? 'Sending…' : 'Submit Application'}
            </button>
            {status === 'sent' && (
              <p className="t-sm" style={{ color: '#7fa98c' }}>Thanks — we've received your application.</p>
            )}
            {status === 'error' && (
              <p className="t-sm" style={{ color: '#d4674a' }}>
                Something went wrong. Please email us directly at contact@ufeus.com.
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
